import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const INTERIOR_SINKS = "https://customer-assets.emergentagent.com/job_turnkey-deploy/artifacts/2gm61wlj_kit%20-01.jpg";
const INTERIOR_STOVES = "https://customer-assets.emergentagent.com/job_turnkey-deploy/artifacts/cennhf6l_kit%20-02.jpg";
const UNIT_RENDER = "https://customer-assets.emergentagent.com/job_turnkey-deploy/artifacts/cb6fz8z9_shipping-container-restaurant-.jpg";

const benefits = [
  "Turnkey, code-compliant restaurant shell",
  "Faster opening = faster revenue",
  "Lower upfront capital",
  "Flexible lease structures",
  "Prime sites sourced and pre-qualified",
  "Infrastructure designed for high-volume operations",
];

const steps = [
  { num: "01", title: "Operator Fit", desc: "We assess your concept, requirements, and growth targets." },
  { num: "02", title: "Site Match", desc: "We identify and qualify the right PadSite for your operation." },
  { num: "03", title: "Unit Delivery", desc: "A turnkey, code-compliant restaurant shell delivered to your specs." },
  { num: "04", title: "Move-In & Open", desc: "Finish your interior and start generating revenue." },
];

export default function OperatorsPage() {
  return (
    <div>
      {/* Hero */}
      <section
        data-testid="operators-hero"
        className="relative overflow-hidden"
        style={{ backgroundColor: "#2d2d2d" }}
      >
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url(${UNIT_RENDER})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(45,45,45,0.55)" }} />

        <div className="relative w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 pt-28 pb-14 md:py-20">
          <div className="max-w-3xl">
            <p
              className="text-xs uppercase tracking-[0.25em] font-semibold mb-5 opacity-0 animate-fade-up"
              style={{ color: "#d4782f" }}
            >
              For Operators
            </p>
            <h1
              className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.08] mb-5 opacity-0 animate-fade-up animate-delay-1"
              style={{ color: "#FFFFFF" }}
            >
              Open Your Next Location Without Construction Delays.
            </h1>
            <p
              className="text-base md:text-lg leading-relaxed max-w-2xl opacity-0 animate-fade-up animate-delay-2"
              style={{ color: "rgba(255,255,255,0.75)" }}
            >
              We deliver a fully code-compliant restaurant shell so you can
              focus on operations, not buildout.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section
        data-testid="operator-benefits"
        style={{ backgroundColor: "#F4F5F7" }}
      >
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <p
            className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
            style={{ color: "#d4782f" }}
          >
            Operator Advantages
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.2] mb-8"
            style={{ color: "#1A1D23" }}
          >
            Why Operators Choose PadSite
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-5">
            {benefits.map((b, i) => (
              <div
                key={i}
                data-testid={`operator-benefit-${i}`}
                className="flex items-start gap-4"
              >
                <div
                  className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                  style={{ backgroundColor: "#d4782f" }}
                />
                <p
                  className="text-base md:text-lg leading-relaxed"
                  style={{ color: "#1A1D23" }}
                >
                  {b}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Operator Launch Budget */}
      <section data-testid="operator-budget" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold mb-4" style={{ color: "#d4782f" }}>
            Startup Investment
          </p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.2] mb-8" style={{ color: "#1A1D23" }}>
            Lease Unit - $ 50K To Open The Doors
          </h2>

          <div className="max-w-2xl">
            <table className="w-full text-sm">
              <tbody>
                {[
                  ["Security Deposit", "$4,000"],
                  ["Your Monthly Rent - Atlanta Market", "$4,000"],
                  ["Admin / Insurance", "$2,000"],
                  ["Hood System & Grease Trap", "INCLUDED ($0)"],
                  ["New Equipment – Sinks", "$3,300"],
                  ["New Refrigeration / Freezers", "$11,800"],
                  ["New Cooking Equipment", "$8,400"],
                  ["New Misc. Equipment / Shelving / Racks", "$1,900"],
                  ["New Smallwares, Utensils & Shelving", "$3,000"],
                  ["Misc. Admin – POS, Utilities, Advertising, Signage", "$6,600"],
                  ["Contingency", "$5,000"],
                ].map(([item, cost], i) => (
                  <tr key={i} className="border-b" style={{ borderColor: "#16191E" }}>
                    <td className="py-3 pr-4" style={{ color: "#1A1D23" }}>
                      <div className="flex items-center gap-3">
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: "#d4782f" }} />
                        {item}
                      </div>
                    </td>
                    <td className="py-3 text-right font-bold whitespace-nowrap" style={{ color: cost.includes("INCLUDED") ? "#4EA72E" : "#0E2841" }}>
                      {cost}
                    </td>
                  </tr>
                ))}
                <tr style={{ borderTop: "2px solid #d4782f" }}>
                  <td className="py-4 pr-4 font-bold text-base" style={{ color: "#1A1D23" }}>Total Start-up Cost</td>
                  <td className="py-4 text-right font-bold text-xl" style={{ color: "#d4782f" }}>$50K</td>
                </tr>
              </tbody>
            </table>
            <p className="text-sm mt-4 font-semibold" style={{ color: "#4A4F5C" }}>(Unit Ready to Operate)</p>
          </div>
        </div>
      </section>

      {/* Time-to-Open Comparison */}
      <section
        data-testid="operator-speed"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <p
            className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
            style={{ color: "#d4782f" }}
          >
            The Speed Advantage
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.2] mb-10"
            style={{ color: "#1A1D23" }}
          >
            Cut Your Time-to-Open by 6-12 Months.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* Traditional */}
            <div className="p-8 md:p-10 border" style={{ borderColor: "rgba(26,29,35,0.1)", backgroundColor: "#F4F5F7" }}>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-6" style={{ color: "#A1A5B0" }}>
                Traditional Build
              </p>
              <span className="text-5xl md:text-6xl font-bold block mb-3" style={{ color: "rgba(26,29,35,0.2)" }}>
                12-18
              </span>
              <p className="text-base" style={{ color: "#4A4F5C" }}>Months to open</p>
              <div className="mt-6 space-y-2">
                {["Permitting delays", "Construction overruns", "Inspection bottlenecks", "Capital at risk during build"].map((item, i) => (
                  <p key={i} className="text-sm flex items-center gap-2" style={{ color: "#A1A5B0" }}>
                    <span style={{ color: "#e74c3c" }}>x</span> {item}
                  </p>
                ))}
              </div>
            </div>

            {/* PadSite */}
            <div className="p-8 md:p-10 border-2" style={{ borderColor: "#d4782f", backgroundColor: "rgba(212,120,47,0.04)" }}>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold mb-6" style={{ color: "#d4782f" }}>
                PadSite Partners
              </p>
              <span className="text-5xl md:text-6xl font-bold block mb-3" style={{ color: "#d4782f" }}>
                30-60
              </span>
              <p className="text-base" style={{ color: "#1A1D23" }}>Days to open</p>
              <div className="mt-6 space-y-2">
                {["Pre-engineered & code-compliant", "No permitting surprises", "Turnkey kitchen infrastructure", "Minimal capital at risk"].map((item, i) => (
                  <p key={i} className="text-sm flex items-center gap-2" style={{ color: "#4A4F5C" }}>
                    <span style={{ color: "#4EA72E" }}>&#10003;</span> {item}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Base Unit */}
      <section
        data-testid="operator-base-unit"
        style={{ backgroundColor: "#F4F5F7" }}
      >
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <p
            className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
            style={{ color: "#d4782f" }}
          >
            Unit Delivery
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.2] mb-6"
            style={{ color: "#1A1D23" }}
          >
            Our Base Unit
          </h2>
          <p
            className="text-base md:text-lg leading-relaxed max-w-3xl mb-8"
            style={{ color: "#4A4F5C" }}
          >
            Every PadSite starts with a 40ft code-compliant container shell — FRP walls, hub drains,
            plug locations, exhaust hood with fire suppression, mini split HVAC, employee bathroom,
            kitchen-rated flooring, and integrated grease trap.
          </p>
          <div className="grid grid-cols-1 gap-6">
            <div className="relative overflow-hidden bg-white">
              <img
                src="https://customer-assets-4nw71qhi.emergentagent.net/job_turnkey-deploy/artifacts/h0n593bm_ChatGPT%20Image%20Sep%201%2C%202026%2C%2007_42_04%20PM.png"
                alt="PadSite base unit — orange and black container kitchen with digital menu boards"
                className="w-full h-auto"
                data-testid="base-unit-hero"
              />
            </div>
            <div className="relative overflow-hidden bg-white">
              <img
                src="https://customer-assets-4nw71qhi.emergentagent.net/job_turnkey-deploy/artifacts/0ob4mwry_hd-photo-1-40%20ft%20container%20hood%20wall.webp"
                alt="40ft container kitchen — hood wall, HVAC, grease trap, bathroom"
                className="w-full h-auto"
                data-testid="base-unit-equipped"
              />
            </div>
            <div className="relative overflow-hidden bg-white">
              <img
                src="https://customer-assets-4nw71qhi.emergentagent.net/job_turnkey-deploy/artifacts/g059cdlf_hd-photo-1-40%20ft%20container%20-%20window%20wall-02.webp"
                alt="40ft container interior — FRP walls, hub drain, epoxy floors"
                className="w-full h-auto"
                data-testid="base-unit-shell"
              />
            </div>
          </div>
          <p
            className="text-center text-lg md:text-xl font-bold tracking-tight mt-8"
            style={{ color: "#d4782f" }}
          >
            Move-In Ready!<br />Just add your equipment!
          </p>
        </div>
      </section>

      {/* Kitchen Interior Gallery */}
      <section style={{ backgroundColor: "#FFFFFF" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <p
            className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
            style={{ color: "#d4782f" }}
          >
            Inside The Unit
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.2] mb-8"
            style={{ color: "#1A1D23" }}
          >
            Commercial-Grade Kitchen Infrastructure
          </h2>
          <div>
            <div className="relative overflow-hidden">
              <img
                src={INTERIOR_SINKS}
                alt="Commercial kitchen interior - prep and wash stations"
                className="w-full h-auto"
                data-testid="operator-interior-1"
              />
            </div>
            <p
              className="text-center text-base font-bold tracking-tight mt-4"
              style={{ color: "#1A1D23" }}
            >
              Built To Receive<br />Standard Commercial Equipment
            </p>
          </div>
        </div>
      </section>
      <section
        data-testid="operator-process"
        style={{ backgroundColor: "#FFFFFF" }}
      >
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
          <p
            className="text-xs uppercase tracking-[0.25em] font-semibold mb-4"
            style={{ color: "#d4782f" }}
          >
            Our Process
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold tracking-tight leading-[1.2] mb-8 md:mb-10"
            style={{ color: "#1A1D23" }}
          >
            From Interest to Opening — Streamlined.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
            {steps.map((step, i) => (
              <div
                key={i}
                data-testid={`operator-step-${i}`}
                className="relative"
              >
                <span
                  className="text-8xl font-bold leading-none absolute -top-4 left-0"
                  style={{ color: "rgba(14, 40, 65, 0.04)" }}
                >
                  {step.num}
                </span>
                <div className="relative pt-12">
                  <h3
                    className="text-xl md:text-2xl font-bold tracking-tight mb-3"
                    style={{ color: "#1A1D23" }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="text-sm md:text-base leading-relaxed"
                    style={{ color: "#4A4F5C" }}
                  >
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12">
            <Link
              to="/contact"
              data-testid="operator-contact-cta"
              className="inline-flex items-center gap-2 px-8 py-4 font-bold tracking-wide transition-colors rounded-none text-sm"
              style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#E08A45")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#d4782f")}
            >
              Find Your PadSite
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
